//CoolEarthList.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import MainLayout from '../../layouts/MainLayout';

import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';

const MyKaizenList = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [kaizens, setKaizens] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedKaizenId, setSelectedKaizenId] = useState(null);

  const fetchKaizens = useCallback(() => {
    const config = {
      url: "/kaizen/myallkaizen",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id}
    };
    // console.log("My Kaizen Search Payload: ", config);
    fetchData(config, { showSuccessToast: false }).then(data => setKaizens(data.kaizens));
  }, [authState.token, fetchData, authState.user._id]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchKaizens();
  }, [authState.isLoggedIn, fetchKaizens]);

  const handleDelete = (id) => {
    const config = {
      url: `/kaizen/${id}`,
      method: "delete",
      headers: { Authorization: authState.token }
    };
    fetchData(config).then(() => fetchKaizens());
  }

  const handleView = (kaizenId) => {
    setSelectedKaizenId(prevId => (prevId === kaizenId ? null : kaizenId));
  };

  const handleCloseKaizenDetails = () => {
    setSelectedKaizenId(null);
  };

  const handleKaizenClick = (kaizenId) => {
    setSelectedKaizenId(kaizenId);
  };

  const handleKaizenShare = (kaizenId) => {
    setSelectedKaizenId(kaizenId);
  };

  const handleKaizenSave = (kaizenId) => {
    setSelectedKaizenId(kaizenId);
  };

  const handleKaizenPrint = (kaizenId) => {
    setSelectedKaizenId(kaizenId);
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  const sortedKaizens = [...kaizens].sort((a, b) => new Date(b.targetStartDate) - new Date(a.targetStartDate));

  return (
    <>
    <MainLayout>
      <div>
        <h3 className='text-lg mt-1 mx-8 border-b border-b-gray-300 text-white'>My SMART Goal List</h3>
      </div>
      <div className="my-2 mx-auto max-w-[700px] py-4">
        <div className="border border-gray-300 p-4 mt-4">
          <div className="mt-2">
            <div className="mt-2">
              {kaizens && kaizens.length !== 0 && (
                <div className="flex justify-between items-center border-b border-b-gray-300">
                  <div>
                  <h3 className='text-m mt-1 mx-1 text-yellow-200'>{kaizens.length} SMART Goals Found!</h3>
                  </div>
                  <div className='mr-4'>
                  <Link to='/kaizen/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                    <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Add New
                  </Link>
                </div>
              </div>
              )}
            </div>
          </div>
          {loading ? (
            <Loader />
          ) : (
            <div>
              {kaizens && kaizens.length === 0 ? (
                <div className='w-[600px] h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                  <div className="flex justify-between items-center">
                  <div>
                  <h3 className='text-lg mt-1 mx-2 text-orange-400'>No SMART Goal Record Found!</h3>
                  </div>
                  <div>
                  <Link to='/kaizen/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-500'>
                    <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Add New
                  </Link>
                </div>
              </div>
                </div>
              ) : (
                sortedKaizens.map((kaizen, index) => (
                  <div key={kaizen._id} className='bg-white my-1 p-1 text-gray-600 rounded-md shadow-md text-sm'>
                    <div className='flex'>
                      <span className='font-medium'>{index + 1}{'. '}{kaizen.title}{', '}({formatDateTime(kaizen.dateCreated)})</span>
                      <Tooltip text={"View details"} position={"top"}>
                        <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(kaizen._id)}>
                          <i className="fa-solid fa-eye"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Edit this record"} position={"top"}>
                        <Link to={`/kaizen/${kaizen._id}`} className='mr-4 text-green-600 cursor-pointer'>
                          <i className="fa-solid fa-pen"></i>
                        </Link>
                      </Tooltip>
                      <Tooltip text={"Delete this record"} position={"top"}>
                        <span className='text-red-500 cursor-pointer mr-4' onClick={() => handleDelete(kaizen._id)}>
                          <i className="fa-solid fa-trash"></i>
                        </span>
                      </Tooltip>
                    </div>
                    {selectedKaizenId === kaizen._id && (
                      <div className='bg-gray-200 text-black'>
                        <p className='bg-blue-700 text-white justify-center text-m mt-2 mb-2'>
                          <h2 className='text-white'><strong>Kaizen Details</strong></h2>
                        </p>
                        <p className='text-yellow-600'><strong>kaizen SMART Goal Code:</strong> {kaizen.kaizenCode}</p>

                        <p><strong>Title:</strong> {kaizen.title}</p>
                        <p><strong>Description:</strong> {kaizen.description}</p>
                        <p><strong>Category:</strong> {kaizen.kaizenCategory}</p>
                                            
                        <p><strong>Target Start Date:</strong> {formatDateTime(kaizen.targetStartDate)}</p>
                        <p><strong>Target End Date:</strong> {formatDateTime(kaizen.targetEndDate)}</p>
                        <p><strong>Date Created:</strong> {formatDateTime(kaizen.dateCreated)}</p>
                        <p><strong>Remarks:</strong> {kaizen.remarks}</p>
                        <Tooltip text={"Share"} position={"top"}>
                        <span className='text-green-500 cursor-pointer mr-4' onClick={() => handleKaizenShare(kaizen._id)}>
                          <i className="fa-solid fa-share"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Save"} position={"top"}>
                        <span className='text-blue-500 cursor-pointer mr-4' onClick={() => handleKaizenSave(kaizen._id)}>
                          <i className="fa-solid fa-save"></i>
                        </span>
                      </Tooltip>
                      <Tooltip text={"Print"} position={"top"}>
                        <span className='text-orange-500 cursor-pointer mr-4' onClick={() => handleKaizenPrint(kaizen._id)}>
                          <i className="fa-solid fa-print"></i>
                        </span>
                      </Tooltip>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
    </>
  )
}

export default MyKaizenList;
